import { useEffect, useMemo, useState } from 'react'
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro'
import { values } from 'lodash'

import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { fetchPlaidItemsIfNeeded } from '@/actions/plaidItemActions'
import { selectPlaidItems } from '@/selectors/financeSelectors'
import { TypographyStyles } from '@/styles'
import { Button, Dialog, Icon, Image, Link, List, Separator, Text } from '@/ui'
import { PostAuthDialog } from './LinkContext'

export const UpgradeFromManualStatementsDialog = ({
  postAuthDialog,
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
  postAuthDialog: PostAuthDialog
}) => {
  const dispatch = useAppDispatch()
  const plaidItems = useReselector(selectPlaidItems)
  const [fetching, setFetching] = useState(true)

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchPlaidItemsIfNeeded())
      setFetching(false)
    }

    if (isOpen) {
      fetch()
    }
  }, [dispatch, isOpen])

  const manualStatementInstitution = useMemo(() => {
    return values(plaidItems).filter((plaidItem) =>
      plaidItem.accounts?.some(
        (account) =>
          (account.statementPermissions === null ||
            account.statementPermissions === 'plaid_statement_disabled') &&
          !account.bankAccessEnabledAt
      )
    )
  }, [plaidItems])

  if (fetching) return null

  return (
    <Dialog>
      <Dialog.Content>
        <div className="bg-neutral-25 rounded-lg p-6">
          <Dialog.Title>You have automatic statement uploads</Dialog.Title>

          <Image
            src="https://heard-images.s3.amazonaws.com/assets/statement_upload.svg"
            alt="banner"
            height={180}
            width={180}
          />
        </div>

        <Dialog.Description>
          <Text className="mt-4">
            Accounts at <strong>{postAuthDialog.institutionName}</strong>{' '}
            support automatic statement uploads. This means you won&apos;t have
            to manually upload statements every month for accounts at this
            institution.
          </Text>
        </Dialog.Description>

        {manualStatementInstitution.length > 0 && (
          <>
            <Separator />

            <Text variant={TypographyStyles.H3} className="mt-4 text-danger">
              <Icon icon={solid('circle-xmark')} color="danger" size="1x" />{' '}
              Manual statement uploads still needed
            </Text>

            <Text
              variant={TypographyStyles.BODY_XS}
              className="mt-1 text-forest"
            >
              These financial institutions don&apos;t support automatic
              statement uploads. You will still need to upload them manually to
              Documents.
            </Text>

            <List bulleted className="pt-5">
              {manualStatementInstitution.map((plaidItem) => (
                <List.Item key={plaidItem.id}>
                  {plaidItem.institutionName}
                </List.Item>
              ))}
            </List>
          </>
        )}

        <div className="mt-4">
          <Link
            newPage
            href="https://support.joinheard.com/hc/en-us/articles/23569956149399/"
            variant="secondary"
          >
            Learn more about statement uploads &#8594;
          </Link>
        </div>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}
